import { pickNearestNode, pickPaddedNode } from './pickPaddedNode';

const CLICK_PX2 = 36;
const POINTER_CAPTURE = true;

function taskIdFromNodeTag(target) {
  if (!target?.closest) return null;
  const tag = target.closest('.neural-view__node-tag');
  if (tag && !tag.closest('.neural-view__hover-name')) return tag.dataset.taskId || null;
  const wrapped = target.querySelector?.(':scope > .neural-view__node-tag');
  if (wrapped && !wrapped.closest('.neural-view__hover-name')) return wrapped.dataset.taskId || null;
  return null;
}

/** Click, hover, drag slop, and wheel zoom. OrbitControls still gets the real pointerdown. */
export function bindScenePointer({
  container,
  canvas,
  camera,
  controls,
  meshByTaskId,
  nodeGroup,
  raycasterCtx,
  interactionRef,
  hostRect,
  setSelectedMissionIdRef,
  syncSelection,
  pauseIdle,
  focusTargetGoalRef,
  focusCameraGoalRef,
  targetDistRef,
  MIN_DIST,
  MAX_DIST,
  zoomCursorRef,
  zoomDriveRef,
  ZOOM_IMPULSE,
}) {
  const pointerDownPx = { x: 0, y: 0 };

  const onWheel = (e) => {
    e.preventDefault();
    focusTargetGoalRef.v = focusCameraGoalRef.v = null;
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;
    else if (e.deltaMode === 2) dy *= 800;
    const notches = dy > 300 ? 3 : dy < -300 ? -3 : dy / 100;
    if (zoomDriveRef) zoomDriveRef.vel += notches * (ZOOM_IMPULSE || 0.95);
    const rect = container.getBoundingClientRect();
    const w = Math.max(rect.width, 1);
    const h = Math.max(rect.height, 1);
    if (zoomCursorRef) {
      const nx = ((e.clientX - rect.left) / w) * 2 - 1;
      const ny = -((e.clientY - rect.top) / h) * 2 + 1;
      if (
        !zoomCursorRef.live
        || Math.abs(nx - zoomCursorRef.x) > 0.03
        || Math.abs(ny - zoomCursorRef.y) > 0.03
      ) {
        zoomCursorRef.hasPivot = false;
      }
      zoomCursorRef.x = nx;
      zoomCursorRef.y = ny;
      zoomCursorRef.live = true;
    }
    raycasterCtx.lastPointer.x = e.clientX;
    raycasterCtx.lastPointer.y = e.clientY;
  };

  const selectMission = (taskId) => {
    const mesh = meshByTaskId.get(taskId);
    if (!mesh || mesh.userData.exiting) return;
    const id = mesh.userData.nodeMeta?.rootTaskId ?? mesh.userData.taskId ?? taskId;
    setSelectedMissionIdRef.current(id);
    syncSelection(id);
  };

  const onCanvasPointerDown = (e) => {
    if (e.target?.closest?.('.neural-view__nebula-tag')) return;
    pointerDownPx.x = e.clientX;
    pointerDownPx.y = e.clientY;
    if (taskIdFromNodeTag(e.target)) return;
    interactionRef.pointerHeld = true;
  };

  const absorbClickJitter = (e) => {
    controls._panStart?.set(e.clientX, e.clientY);
    controls._rotateStart?.set(e.clientX, e.clientY);
    controls._dollyStart?.set(e.clientX, e.clientY);
  };

  const beginRealDrag = () => {
    if (interactionRef.dragging) return;
    interactionRef.dragging = true;
    pauseIdle();
    focusTargetGoalRef.v = focusCameraGoalRef.v = null;
    canvas.style.cursor = 'grabbing';
    container.style.cursor = 'grabbing';
  };

  const onCanvasClick = (e) => {
    if (e.target?.closest?.('.neural-view__nebula-tag')) return;
    const dx = e.clientX - pointerDownPx.x;
    const dy = e.clientY - pointerDownPx.y;
    if (dx * dx + dy * dy > CLICK_PX2) return;
    if (interactionRef.dragging) return;
    const fromTag = taskIdFromNodeTag(e.target);
    if (fromTag) {
      selectMission(fromTag);
      return;
    }
    const rect = container.getBoundingClientRect();
    camera.updateMatrixWorld(true);
    nodeGroup.updateMatrixWorld(true);
    const hit = pickPaddedNode(camera, meshByTaskId, e.clientX, e.clientY, rect);
    if (hit) {
      const ud = hit.userData;
      const id = ud.nodeMeta?.rootTaskId ?? ud.taskId;
      setSelectedMissionIdRef.current(id);
      syncSelection(id);
    } else {
      setSelectedMissionIdRef.current(null);
      syncSelection(null);
    }
  };

  const applyHoverRaycast = (clientX, clientY) => {
    if (hostRect.width < 1 || hostRect.height < 1) return;
    const fromTag = taskIdFromNodeTag(document.elementFromPoint(clientX, clientY));
    camera.updateMatrixWorld(true);
    const id = fromTag
      ?? pickNearestNode(camera, meshByTaskId, clientX, clientY, hostRect)?.userData.taskId
      ?? null;
    if (id !== raycasterCtx.hoverPointerId) {
      raycasterCtx.hoverPointerId = id;
      canvas.style.cursor = id ? 'pointer' : 'grab';
    }
  };

  const onCanvasPointerMove = (e) => {
    raycasterCtx.lastPointer.x = e.clientX;
    raycasterCtx.lastPointer.y = e.clientY;
    interactionRef.pointerAt = performance.now();
    if (interactionRef.pointerHeld || interactionRef.dragging) {
      const dx = e.clientX - pointerDownPx.x;
      const dy = e.clientY - pointerDownPx.y;
      if (!interactionRef.dragging) {
        if (dx * dx + dy * dy > CLICK_PX2) beginRealDrag();
        else absorbClickJitter(e);
      }
    }
  };

  const onCanvasPointerLeave = () => {
    raycasterCtx.hoverPointerId = null;
    raycasterCtx.lastPointer.x = null;
    raycasterCtx.lastPointer.y = null;
    interactionRef.pointerAt = 0;
    canvas.style.cursor = 'grab';
  };

  const resyncHoverFromLastPointer = () => {
    const { x, y } = raycasterCtx.lastPointer;
    if (x == null || y == null) return;
    applyHoverRaycast(x, y);
  };

  const onCanvasPointerUp = () => {
    interactionRef.pointerHeld = false;
    if (!interactionRef.dragging) resyncHoverFromLastPointer();
  };

  container.addEventListener('wheel', onWheel, { passive: false });
  container.addEventListener('pointerdown', onCanvasPointerDown, POINTER_CAPTURE);
  container.addEventListener('click', onCanvasClick, POINTER_CAPTURE);
  container.addEventListener('pointermove', onCanvasPointerMove, POINTER_CAPTURE);
  container.addEventListener('pointerleave', onCanvasPointerLeave, POINTER_CAPTURE);
  window.addEventListener('pointerup', onCanvasPointerUp, true);
  window.addEventListener('pointercancel', onCanvasPointerUp, true);
  const onControlsEnd = () => {
    const wasDragging = interactionRef.dragging;
    interactionRef.dragging = false;
    interactionRef.pointerHeld = false;
    if (wasDragging) pauseIdle();
    resyncHoverFromLastPointer();
  };
  controls.addEventListener('end', onControlsEnd);

  const disposePointer = () => {
    container.removeEventListener('wheel', onWheel);
    container.removeEventListener('pointerdown', onCanvasPointerDown, POINTER_CAPTURE);
    container.removeEventListener('click', onCanvasClick, POINTER_CAPTURE);
    container.removeEventListener('pointermove', onCanvasPointerMove, POINTER_CAPTURE);
    container.removeEventListener('pointerleave', onCanvasPointerLeave, POINTER_CAPTURE);
    window.removeEventListener('pointerup', onCanvasPointerUp, true);
    window.removeEventListener('pointercancel', onCanvasPointerUp, true);
    controls.removeEventListener('end', onControlsEnd);
  };

  return { applyHoverRaycast, disposePointer };
}
